import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  #intersectionObserver;
  #nearViewport = false;
  #activeFetch;
  #activeKey;
  #generation = 0;
  #cache = new Map();
  #mutationObserver = new MutationObserver(() => {
    if (this.#nearViewport) void this.#loadRecommendations();
    else {
      this.#generation++;
      this.#activeFetch?.abort();
      this.#activeFetch = null;
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-loading');
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#nearViewport = false;
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-section-id', 'data-intent', 'data-url', 'data-recommendations-performed'],
    });
    const connection = navigator.connection;
    const constrained = connection?.saveData || /(^|-)2g$|^3g$/.test(connection?.effectiveType || '');
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver((entries) => {
        this.#nearViewport = entries.some((entry) => entry.isIntersecting);
        if (this.#nearViewport) void this.#loadRecommendations();
      }, { rootMargin: `0px 0px ${constrained ? 100 : 400}px 0px` });
      this.#intersectionObserver.observe(this);
    } else {
      this.#nearViewport = true;
      void this.#loadRecommendations();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver.disconnect();
    this.#generation++;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    if (recommendationsPerformed === 'true' || !this.isConnected || !this.#nearViewport) return;
    if (!productId || !sectionId || !this.dataset.url) return;
    const url = new URL(this.dataset.url, location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent || 'related');
    const key = url.href;
    if (this.#activeFetch && this.#activeKey === key) return;
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeKey = key;
    this.#activeFetch = controller;
    const generation = ++this.#generation;
    const current = () => this.isConnected && generation === this.#generation && this.dataset.productId === productId;
    this.classList.remove('hidden');
    delete this.dataset.error;
    this.setAttribute('aria-busy', 'true');
    this.setAttribute('data-loading', '');
    // A failed optional section must not retain its skeleton indefinitely.
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      let html = this.#cache.get(key);
      if (!html) {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        html = await response.text();
        if (!current()) return;
        if (!html.trim()) throw new Error('No recommendations available');
        if (this.#cache.size >= 4) this.#cache.delete(this.#cache.keys().next().value);
        this.#cache.set(key, html);
      }
      if (!current()) return;
      this.dataset.recommendationsPerformed = 'true';
      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (!current()) return;
      this.classList.add('hidden');
      this.dataset.error = 'Error loading product recommendations';
      console.warn('Product recommendations unavailable:', error.message);
    } finally {
      clearTimeout(timeout);
      if (current()) {
        this.#activeFetch = null;
        this.removeAttribute('aria-busy');
        this.removeAttribute('data-loading');
      }
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
